import store from './store/store'
import axios from 'axios'
import {app} from './main'
import * as Sentry from '@sentry/browser'
import router from './router'
import {constants} from '@/utils/Constants.js'

const baseURL = `${process.env.VUE_APP_API_URL}`
const API_VERSION = 'api/v3/'

const routesToExcludeFromAccountParameter = [
  '/login',
  '/register',
  '/password/email',
  '/password/reset',
  '/email/resend',
  '/logout',
  '/sanctum/csrf-cookie'
]

const baseAxios = axios.create({
  baseURL,
  withCredentials: true
  // You can add your headers here
})

// set ajax requests counter in the window
window.pendingAjaxRequests = 0

const checkAjaxRequests = () => {
  window.pendingAjaxRequests--

  if (window.pendingAjaxRequests < 1) {
    app.$vs.loading.close()
  }
}

const showLoading = () => {
  if (window.pendingAjaxRequests) {
    return
  }

  app.$vs.loading()
}

const setSentryErrorContext  = (error) => {
  const state = store.state,
    user = state.authentication.user || {},
    activeAccount = state.authentication.activeAccount || {},
    statusCode = error && error.response ? error.response.status : 0,
    fullLocation = location,
    destinationUrl = `${error.config.baseURL}/${error.config.url}`
  if (statusCode && statusCode < 500) {
    return error
  }

  Sentry.setContext('extras', {
    'user_id': user.id,
    'user_name': user.name,
    'user_email': user.email,
    
    'active_account_id': activeAccount.id,
    'active_account_name': activeAccount.name,
    
    'current_user_location': fullLocation.toString(),
    'current_destination_url': destinationUrl,
    'current_destination_status_code': statusCode
  })
}

const sendSentryExceptionWithHandledError = (error) => {
  const statusCode = error && error.response ? error.response.status : 0
  if (statusCode && statusCode < 500) {
    return
  }

  setSentryErrorContext(error)
  Sentry.captureException(error)
}

// declare a request interceptor
baseAxios.interceptors.request.use(async config => {

  if (!config.hideLoader) {
    showLoading()
  }

  window.pendingAjaxRequests++
  // config.headers['Speakol-Auth'] = 'hello world' // we need to figure out how to set a valid value that we can validate from backend

  // perform a task before the request is sent
  const activeAccount = localStorage.getItem('activeAccountId')
  if (activeAccount && !routesToExcludeFromAccountParameter.includes(config.url)) {
    config.url = API_VERSION + activeAccount + config.url
  }

  return config
}, error => {
  checkAjaxRequests()
  // handle the error
  return Promise.reject(error)
})

baseAxios.interceptors.response.use((response) => {
  checkAjaxRequests()
  return response
}, async error => {
  checkAjaxRequests()

  // Refresh the csrf-cookie if expired
  if (error.config && error.response && error.response.status === constants.HTTP_STATUS_CODES.PRECONDITION_FAILED) {
    return baseAxios.get('/sanctum/csrf-cookie').then(() => {
      return baseAxios.request(error.config)
    })
  } else if (error.config && error.response && error.response.status === constants.HTTP_STATUS_CODES.SERVICE_UNAVAILABLE) {
    router.push('/maintenance')
  } else if (error.config && error.response && error.response.status === constants.HTTP_STATUS_CODES.UNAUTHORIZED) {
    await store.dispatch('authentication/logout')
    window.location.href = '/login'
  } else if (error.config && error.response && error.response.status === constants.HTTP_STATUS_CODES.CSRF_TOKEN_EXPIRED) {
    // in case of session expired
    window.location.href = '/login'
  } else if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx and not 401
    sendSentryExceptionWithHandledError(error)
    if (error.response.status === constants.HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR) {
      app.isGeneralError = true
    }
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    sendSentryExceptionWithHandledError(error)
  } else {
    // Something happened in setting up the request that triggered an Error
    sendSentryExceptionWithHandledError(error)
  }

  return Promise.reject(error)
})

baseAxios.sendHandledSentryException = sendSentryExceptionWithHandledError

export default baseAxios
